
import { Vue, Component, Mixins, Prop, Inject, Watch } from "vue-property-decorator"
import { CaseField, CaseDetails, CaseAudit, AuditUser, Field } from "@/types"
import CaseAuditHistoryService from "@/services/caseAuditHistoryService"
import UserImage from "../UserImage.vue"
import { caseColumns } from "@/types/caseColumns"
import api from "@/services/api"
import { DataSourceItemSLA } from "@/types/dataSourceItem"
import { TenantFeatures } from "@/types/tenantFeatures"
import { Getter } from "vuex-class"
import ValidationMixin from "@/../src/mixins/validationMixin"
import { kebabCase } from "lodash"
import htmlToFormattedText from "html-to-formatted-text"

interface FieldAuditHistoryItem extends Omit<CaseAudit, "changedBy"> {
	changedBy: AuditUser | null,
	nextChangedOccurredAt: string | null
}

interface FieldMetricItem {
	value: string | null,
	totalTime: number,
	totalTimeString: string,
	hasExceededSLA: boolean,
	slaForecastString: string | null
	slaDefinitionString: string | null
}

@Component({
	components: {
		"user-image": UserImage
	}
})
export default class Metrics extends Mixins(ValidationMixin) {
	@Inject() caseAuditHistoryService!: CaseAuditHistoryService

	@Prop({ type: String, required: true })
	watchFieldId!: string | null

	@Prop({ type: Object, required: true })
	currentCase!: CaseDetails

	@Prop({ type: Boolean, default: false })
	expandByDefault!: boolean

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter getCaseName!: () => string

	// Constants
	kebabCase = kebabCase
	displayDateFromISOString = ((this as any).displayDateFromISOString)
	displayDateOnlyFromISOString = ((this as any).displayDateOnlyFromISOString)
	getFormattedTimeSpanDisplay = ((this as any).getFormattedTimeSpanDisplay)
	getFormattedTimeSpanBetweenDatesDisplay = ((this as any).getFormattedTimeSpanBetweenDatesDisplay)
	stringMatchesFriendlyDateAndTimeFormat = ((this as any).stringMatchesFriendlyDateAndTimeFormat)

	// Whether the component is expanded or collapsed
	private expanded: boolean = this.expandByDefault

	// The number of seconds between each forced update of computed properties to keep time metrics up to date
	private ForceUpdateFrequencySecs = 60

	// The timer used to trigger regular updates of computed properties
	private updateTimer: NodeJS.Timeout | null = null

	// Triggers computed properties to update on a regular basis when flipped
	private forceUpdateHook: boolean = false

	// The SLAs for items on the Data Source if watching a list-type field
	private sourceSLAs: DataSourceItemSLA[] | null = null

	private async mounted () {
		if (this.getIsFeatureEnabled(TenantFeatures.FieldMetricsSLAs) &&
			(this.watchFieldIsListType && this.watchField?.field.dataSourceId)
		) {
			// load any SLAs for items on the Data Source
			this.sourceSLAs = await api.getSourceItemSLAs(this.watchField.field.dataSourceId)
		}
	}

	/**
	 * The field being watched for changes
	 */
	private get watchField (): CaseField | null {
		if (!this.watchFieldId) return null

		if (this.watchFieldIsCaseColumn) {
			const caseColumn = caseColumns.firstOrDefault(c => c.key === this.watchFieldId)

			if (caseColumn) {
				return {
					value: this.currentCase[caseColumn.key]?.toString() ?? null,
					label: (caseColumn.displayKey ? this.currentCase[caseColumn.displayKey] : this.tryFormatRaw(this.currentCase[caseColumn.key], false))?.toString() ?? null,
					field: {
						id: caseColumn.key,
						title: caseColumn.label,
						dataType: caseColumn.dataType ?? null
					} as Field
				} as CaseField
			}
		}

		return Object.values(this.currentCase.fields).flat(1)
			.firstOrDefault(f => f.field.id === this.watchFieldId) ?? null
	}

	/**
	 * Whether the field being watched is a Case column (e.g. title), otherwise it's a Case Field
	 */
	private get watchFieldIsCaseColumn (): boolean {
		return caseColumns.some(c => c.key === this.watchFieldId)
	}

	private get watchFieldIsListType (): boolean {
		if (!this.watchField?.field.dataType) return false

		return ["list", "list-multiple"].includes(this.watchField.field.dataType)
	}

	/**
	 * The formatted history of changes to the field being watched
	 */
	private get fieldAuditHistory (): FieldAuditHistoryItem[] {
		switch (this.watchField?.field.dataType) {
			case "user-role":
				throw new Error("User Role Field type not implemented yet")
			case "file-upload":
				throw new Error("File Upload Field type not implemented yet")
		}

		const auditHistory = this.caseAuditHistoryService?.auditHistory ?? []

		// filter audit history to only changes to the field being watched. Add time of following change to each item
		let fieldHistory = auditHistory
			.filter(a => (a.fieldId === this.watchFieldId) ||
				(!a.fieldId && this.watchFieldIsCaseColumn && a.fieldName === this.watchField?.field.title)
			).reverse()
			.map((auditValue, index, filteredHistory) => {
				const historyItem = {
					...auditValue,
					nextChangedOccurredAt: index === filteredHistory.length - 1
						? null
						: filteredHistory[index + 1].changedAt
				} as FieldAuditHistoryItem

				if (historyItem.oldValue && !historyItem.oldValueLabel) {
					historyItem.oldValueLabel = this.tryFormatRaw(historyItem.oldValue)
				}

				if (historyItem.newValue && !historyItem.newValueLabel) {
					historyItem.newValueLabel = this.tryFormatRaw(historyItem.newValue)
				}

				return historyItem
			})

		const initialHistoryItem = {
			id: "initial",
			fieldId: this.watchFieldIsCaseColumn ? this.watchField?.label : this.watchFieldId,
			changedAt: this.currentCase.created ?? null,
			changedBy: this.currentCase.createdBy ?? {
				id: "",
				name: "System"
			} as AuditUser,
			oldValue: null
		} as FieldAuditHistoryItem

		// append initial value to history
		if (fieldHistory.length > 0) {
			fieldHistory.unshift({
				...initialHistoryItem,
				newValue: fieldHistory[0].oldValue ?? null,
				newValueLabel: fieldHistory[0].oldValueLabel?.trim() ?? this.tryFormatRaw(fieldHistory[0].oldValue) ?? null,
				nextChangedOccurredAt: fieldHistory[0].changedAt
			} as FieldAuditHistoryItem)
		} else {
			fieldHistory.push({
				...initialHistoryItem,
				newValue: this.watchField?.value ?? null,
				newValueLabel: this.watchField?.label?.trim() ?? this.tryFormatRaw(this.watchField?.value) ?? null,
				nextChangedOccurredAt: null
			} as FieldAuditHistoryItem)
		}

		return fieldHistory
	}

	/**
	 * Tries to format a raw value as human readable
	 * @param value The value to try to format
	 * @param includeFieldSpecificChecks Whether to include any field-specific formatting checks
	 */
	private tryFormatRaw (value: any, includeFieldSpecificChecks: boolean = true): string | null {
		if (value === null || value === undefined) return null

		let valueString = value.toString()

		// try to parse as raw date. If it is, return formatted date
		if (Date.parse(valueString) > 0) {
			const date = new Date(valueString)

			const parsedDateISOString = date.toISOString().replace(".000", "")
			const parsedValueDate = valueString.replace(".000", "")

			if (!isNaN(date.getTime()) && parsedDateISOString === parsedValueDate) {
				const dateOnly =
					includeFieldSpecificChecks && this.watchField?.field?.dataType === "date"

				const formatted = dateOnly
					? this.displayDateOnlyFromISOString(valueString)
					: this.displayDateFromISOString(valueString)

				if (formatted) {
					return formatted
				}
			}
		}

		if (includeFieldSpecificChecks) {
			switch (this.watchField?.field?.dataType) {
				case "date":
					// remember this is 'date' not 'date-time', so remove the time if present
					if (this.stringMatchesFriendlyDateAndTimeFormat(valueString)) {
						valueString = valueString.split(" ")[0]
					}
					break
				case "text-area":
					// remove any html tags / formatting if watching a rich text Field
					valueString = htmlToFormattedText(valueString)
					break
			}
		}

		return valueString.trim()
	}

	/**
	 * The metrics for each value of the field being watched
	 */
	private get fieldMetrics (): FieldMetricItem[] | null {
		if (!this.watchFieldIsListType) return null

		// used to trigger updates on a regular interval
		if (this.forceUpdateHook) {}

		const groupedHistory = this.fieldAuditHistory.groupBy("newValueLabel")
		const fieldMetrics = Object.entries(groupedHistory).map(([key, value]) => {
			const totalTime = value.reduce((total, audit) => {
				const start = new Date(audit.changedAt)
				const end = new Date(audit.nextChangedOccurredAt ?? Date.now())
				const instanceDuration = (end.getTime() - start.getTime())

				return total + instanceDuration
			}, 0)

			let sourceItemSLA: DataSourceItemSLA | null = null
			let hasExceededSLA = false
			let slaForecastString: string | null = null
			let slaDefinitionString: string | null = null

			if (!!this.sourceSLAs && this.isValidObjectId(value[0].newValue)) {
				sourceItemSLA =
					this.sourceSLAs.firstOrDefault(s => s.dataSourceItemId === value[0].newValue) ?? null

				if (sourceItemSLA) {
					const totalTimeHours = (totalTime / 1000 / 60 / 60)
					hasExceededSLA = sourceItemSLA && totalTimeHours > sourceItemSLA.slaHours

					const slaMs = sourceItemSLA.slaHours * 60 * 60 * 1000

					slaDefinitionString = this.getFormattedTimeSpanDisplay(slaMs)
					slaForecastString = this.getFormattedTimeSpanDisplay(Math.abs(slaMs - totalTime))
				}
			}

			return {
				value: key?.length > 0 ? key : null,
				totalTime,
				totalTimeString: this.getFormattedTimeSpanDisplay(totalTime),
				hasExceededSLA,
				slaForecastString,
				slaDefinitionString
			}
		})

		return fieldMetrics
	}

	/**
	 * The value of the field being watched that has the highest totalTime
	 */
	private get fieldMetricsHighestTime (): { value: string | null, totalTime: number } | null {
		if (!this.fieldMetrics) return null

		// force an update on a regular interval
		// eslint-disable-next-line no-unused-expressions
		this.forceUpdateHook

		return this.fieldMetrics.reduce((highest, current) => {
			if (current.totalTime > highest.totalTime) {
				return current
			}

			return highest
		}, this.fieldMetrics[0])
	}

	/**
	 * The total time of all metrics for the Field being watched in ms
	 */
	private get fieldMetricsTotalTimeMs (): number {
		if (!this.fieldMetrics) return 0

		// force an update on a regular interval
		// eslint-disable-next-line no-unused-expressions
		this.forceUpdateHook

		return this.fieldMetrics.reduce((total, current) => {
			return total + current.totalTime
		}, 0)
	}

	private get hasFieldMetrics (): boolean {
		return this.fieldMetrics !== null && this.fieldMetrics.length > 0
	}

	/**
	 * Triggers metrics to update on a regular interval
	 * @param secsUntilUpdate The number of seconds until the next update
	 */
	@Watch("forceUpdateHook", { immediate: true })
	private async forceUpdateHookUpdated () {
		if (this.updateTimer) {
			clearTimeout(this.updateTimer)
		}

		if (!this.hasFieldMetrics) return

		this.updateTimer = setTimeout(() => {
			this.forceUpdateHook = !this.forceUpdateHook
		}, this.ForceUpdateFrequencySecs * 1000)
	}

	@Watch("hasFieldMetrics")
	private hasFieldMetricsUpdated (newValue: boolean) {
		if (newValue) {
			// kick off the regular update timer if there are now metrics
			this.forceUpdateHook = !this.forceUpdateHook
		}
	}

	/**
	 * Gets the percentage of a value compared to a total to 1 decimal place
	 * @param value The value to get the percentage of
	 * @param total The total to compare the value to
	 */
	private getPercentage (value: number, total: number): number {
		return Math.round((value / total) * 1000) / 10
	}

	/**
	 * Gets the tooltip text for the duration of a metric item including the percentage of total time
	 * and the SLA target if applicable
	 * @param metricItem The metric item to get the tooltip text for
	 */
	private getMetricItemDurationTooltipText (metricItem: FieldMetricItem): string {
		if (!metricItem) return ""

		let result = `Set for ~ ${this.getPercentage(metricItem.totalTime, this.fieldMetricsTotalTimeMs)}% of Total Time Since Creation`

		if (metricItem.slaDefinitionString) {
			result += `\n\n<strong>'${metricItem.value}'</strong> SLA Target: <strong>${metricItem.slaDefinitionString}</strong>`
		}

		return result
	}

	private beforeUnmount () {
		if (this.updateTimer) {
			clearTimeout(this.updateTimer)
		}
	}
}
